import React, { useRef, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import { advancedColors } from '../theme'
import { PanelSpan, spanForColumns } from './spans'

type Props = {
  cellRef: React.RefObject<HTMLElement | null>
  gridRef: React.RefObject<HTMLElement | null>
  onPreview: (span: PanelSpan | null) => void
  onCommit: (span: PanelSpan) => void
  label: string
}

// `pitch` is one column plus one gap, so a span of `c` columns is `c * pitch - gap` wide — the
// relation `columnsFor` inverts.
type Tracking = { startX: number; startWidth: number; gridColumns: number; pitch: number; gap: number }

const columnsFor = ({ startWidth, startX, pitch, gap }: Tracking, clientX: number) =>
  Math.round((startWidth + (clientX - startX) + gap) / pitch)

const measure = (cell: HTMLElement, grid: HTMLElement, startX: number): Tracking | null => {
  // Resolved track sizes, not the `repeat()` we asked for — the live count picks the span table.
  const tracks = window.getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean)
  const gridColumns = tracks.length
  const columnWidth = Number.parseFloat(tracks[0])
  const gridWidth = grid.getBoundingClientRect().width

  if (gridColumns < 2 || !Number.isFinite(columnWidth) || !gridWidth) {
    return null
  }

  const gap = (gridWidth - columnWidth * gridColumns) / (gridColumns - 1)

  return { startX, startWidth: cell.getBoundingClientRect().width, gridColumns, pitch: columnWidth + gap, gap }
}

const ResizeGrip = ({ cellRef, gridRef, onPreview, onCommit, label }: Props) => {
  const trackingRef = useRef<Tracking | null>(null)
  const [previewSpan, setPreviewSpan] = useState<PanelSpan | null>(null)

  const preview = (span: PanelSpan | null) => {
    setPreviewSpan(span)
    onPreview(span)
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !cellRef.current || !gridRef.current) {
      return
    }

    const tracking = measure(cellRef.current, gridRef.current, event.clientX)
    if (!tracking) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    trackingRef.current = tracking
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const tracking = trackingRef.current
    if (!tracking) {
      return
    }

    const span = spanForColumns(columnsFor(tracking, event.clientX), tracking.gridColumns)
    if (span && span !== previewSpan) {
      preview(span)
    }
  }

  const onPointerUp = () => {
    if (!trackingRef.current) {
      return
    }

    trackingRef.current = null
    if (previewSpan) {
      onCommit(previewSpan)
    }
    preview(null)
  }

  return (
    <Tooltip title={label}>
      <Box
        // `PanelMenu`'s width buttons are the accessible path for this (ADR-023).
        aria-hidden
        className="AdvancedResizeGrip"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: -6,
          width: 12,
          zIndex: 2,
          cursor: 'col-resize',
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: previewSpan ? 1 : 0,
          transition: 'opacity 120ms ease-in-out',
          '&::after': {
            content: '""',
            width: 4,
            height: 32,
            borderRadius: 999,
            backgroundColor: advancedColors.accent,
          },
        }}
      />
    </Tooltip>
  )
}

export default ResizeGrip
